import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { X, Check, Loader } from 'lucide-react';

// ── Favorites helpers (localStorage cache — kept for Dashboard compatibility) ──
export const getFavorites = () => {
  try { return JSON.parse(localStorage.getItem('favoritePlayers') || '[]'); }
  catch { return []; }
};
export const saveFavorites = (players) => {
  localStorage.setItem('favoritePlayers', JSON.stringify(players));
};
export const toggleFavorite = (player) => {
  const cur = getFavorites();
  const exists = cur.find(p => p.id === player.id);
  const updated = exists
    ? cur.filter(p => p.id !== player.id)
    : [...cur, { id: player.id, name: player.name, pos: player.pos, team: player.team }];
  saveFavorites(updated);
  return updated;
};
export const isFavorited = (playerId) => getFavorites().some(p => p.id === playerId);

const getPlayerInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(part => part[0]).join('').toUpperCase();
};

const Profile = () => {
  const navigate = useNavigate();

  // ── Profile state ─────────────────────────────────────────────────────────
  const [profile, setProfile]       = useState({ firstName: '', lastName: '', email: '', role: '', createdAt: null });
  const [draft, setDraft]           = useState(profile);
  const [isEditing, setIsEditing]   = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [savedToast, setSavedToast] = useState(false);
  const [saveError, setSaveError]   = useState('');

  // ── Password state ────────────────────────────────────────────────────────
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwForm, setPwForm]         = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError]       = useState('');
  const [pwSuccess, setPwSuccess]   = useState(false);
  const [pwLoading, setPwLoading]   = useState(false);

  // ── Favorites state ───────────────────────────────────────────────────────
  const [favorites, setFavorites]   = useState([]);
  const [favsLoading, setFavsLoading] = useState(true);

  // ── Notification state (local only for now) ───────────────────────────────
  const [notifications] = useState({
    injuries: true, lineups: true, projections: true, weekly: false, system: true,
  });

  // ── Load profile from API on mount ───────────────────────────────────────
  useEffect(() => {
    api.getMe()
      .then((data) => {
        if (!data) return;
        const p = {
          firstName: data.first_name,
          lastName:  data.last_name,
          email:     data.email,
          role:      data.role,
          createdAt: data.created_at,
        };
        setProfile(p);
        setDraft(p);
        // Keep localStorage in sync for Navbar initials etc.
        localStorage.setItem('user_first_name', data.first_name);
      })
      .catch(() => {
        // Fallback to localStorage if API unavailable
        const p = {
          firstName: localStorage.getItem('user_first_name') || 'User',
          lastName:  '',
          email:     '',
          role:      localStorage.getItem('user_role') || 'member',
          createdAt: null,
        };
        setProfile(p);
        setDraft(p);
      })
      .finally(() => setProfileLoading(false));
  }, []);

  // ── Load favorites from API ───────────────────────────────────────────────
  useEffect(() => {
    api.getFavorites()
      .then((favs) => {
        if (!favs) return;
        setFavorites(favs);
        // Sync to localStorage cache
        saveFavorites(favs.map(f => ({ id: f.player_id, name: f.player_name, pos: f.position, team: f.team })));
      })
      .catch(() => setFavorites(getFavorites().map(f => ({ player_id: f.id, player_name: f.name, position: f.pos, team: f.team }))))
      .finally(() => setFavsLoading(false));
  }, []);

  // ── Profile handlers ──────────────────────────────────────────────────────
  const handleEdit = () => { setDraft(profile); setIsEditing(true); setSaveError(''); };
  const handleCancel = () => { setDraft(profile); setIsEditing(false); setSaveError(''); };

  const handleSave = async () => {
    setSaveError('');
    try {
      const updated = await api.updateMe({
        first_name: draft.firstName,
        last_name:  draft.lastName,
        email:      draft.email,
      });
      if (!updated) return;
      const p = { firstName: updated.first_name, lastName: updated.last_name, email: updated.email, role: updated.role, createdAt: updated.created_at };
      setProfile(p);
      setDraft(p);
      localStorage.setItem('user_first_name', updated.first_name);
      setIsEditing(false);
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2000);
    } catch (err) {
      setSaveError(err.message || 'Failed to save changes');
    }
  };

  const handleField = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  // ── Password handlers ─────────────────────────────────────────────────────
  const handlePasswordChange = async () => {
    setPwError('');
    if (pwForm.next !== pwForm.confirm) { setPwError('New passwords do not match'); return; }
    if (pwForm.next.length < 8 || !/\d/.test(pwForm.next)) { setPwError('Min 8 chars, must contain a number'); return; }
    setPwLoading(true);
    try {
      await api.changePassword({ current_password: pwForm.current, new_password: pwForm.next });
      setPwSuccess(true);
      setPwForm({ current: '', next: '', confirm: '' });
      setTimeout(() => { setPwSuccess(false); setShowPasswordForm(false); }, 2000);
    } catch (err) {
      setPwError(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  // ── Favorites handlers ────────────────────────────────────────────────────
  const removeFavorite = async (playerId) => {
    // Optimistic
    setFavorites(prev => prev.filter(f => (f.player_id || f.id) !== playerId));
    saveFavorites(getFavorites().filter(f => f.id !== playerId));
    try {
      await api.removeFavorite(playerId);
    } catch {
      // Refetch on failure
      api.getFavorites().then(favs => { if (favs) setFavorites(favs); }).catch(() => {});
    }
  };

  const memberSince = profile.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';

  return (
    <div className="min-h-screen bg-[#050d1f] text-white font-sans pb-20 relative overflow-x-hidden">

      {/* Saved Toast */}
      <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[200] transition-all duration-300 ${savedToast ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
        <div className="flex items-center gap-2.5 text-sm font-bold px-5 py-3 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] border bg-[#0c1526] border-green-500/30 text-green-400">
          <Check size={14} /> Profile saved
        </div>
      </div>

      <Navbar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        <div className="mb-10">
          <h1 className="text-4xl font-bold mb-2">Your <span className="text-[#4d7cff]">Profile</span></h1>
          <p className="text-white/40">Manage your account, favorites, and preferences</p>
        </div>

        {profileLoading ? (
          <div className="flex items-center justify-center py-20 text-white/30">
            <Loader size={20} className="animate-spin mr-3" /> Loading profile...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── Left column ── */}
            <div className="lg:col-span-1 flex flex-col gap-6">

              {/* Avatar card */}
              <div className="bg-[#0c1526] border border-white/[0.08] rounded-[24px] p-6 flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-[#4d7cff] flex items-center justify-center text-2xl font-black mb-4">
                  {profile.firstName?.[0]}{profile.lastName?.[0]}
                </div>
                <div className="font-bold text-lg">{profile.firstName} {profile.lastName}</div>
                <div className="text-white/40 text-sm mb-3">{profile.email}</div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${profile.role === 'admin' ? 'bg-red-500/20 text-red-400' : profile.role === 'analyst' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-[#4d7cff]/20 text-[#4d7cff]'}`}>
                  {profile.role?.charAt(0).toUpperCase() + profile.role?.slice(1)}
                </span>
                <div className="mt-4 text-xs text-white/20">Member since {memberSince}</div>
              </div>

              {/* Notifications */}
              <div className="bg-[#0c1526] border border-white/[0.08] rounded-[24px] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-sm uppercase tracking-wider text-white/50">Notifications</h3>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/[0.06] text-white/30 uppercase tracking-widest">Soon</span>
                </div>
                <div className="space-y-3 opacity-40 pointer-events-none">
                  {Object.entries(notifications).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm capitalize text-white/70">{key === 'projections' ? 'New Projections' : key === 'lineups' ? 'Lineup Reminders' : key === 'injuries' ? 'Injury Alerts' : key === 'weekly' ? 'Weekly Digest' : 'System Updates'}</span>
                      <div className={`w-10 h-5 rounded-full relative ${val ? 'bg-[#4d7cff]' : 'bg-white/10'}`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${val ? 'left-5' : 'left-0.5'}`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right column ── */}
            <div className="lg:col-span-2 flex flex-col gap-6">

              {/* Account info */}
              <div className="bg-[#0c1526] border border-white/[0.08] rounded-[24px] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold">Account Information</h3>
                  {!isEditing ? (
                    <button onClick={handleEdit} className="text-xs font-bold text-[#4d7cff] border border-[#4d7cff]/30 px-4 py-1.5 rounded-xl hover:bg-[#4d7cff]/10 transition-all">
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={handleCancel} className="text-xs font-bold text-white/40 border border-white/10 px-4 py-1.5 rounded-xl hover:bg-white/5 transition-all">
                        Cancel
                      </button>
                      <button onClick={handleSave} className="text-xs font-bold text-white bg-[#4d7cff] px-4 py-1.5 rounded-xl hover:bg-[#3d6cef] transition-all">
                        Save
                      </button>
                    </div>
                  )}
                </div>

                {saveError && <div className="mb-4 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2">{saveError}</div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { label: 'First Name', field: 'firstName', value: draft.firstName },
                    { label: 'Last Name',  field: 'lastName',  value: draft.lastName },
                    { label: 'Email',      field: 'email',     value: draft.email, full: true },
                  ].map(({ label, field, value, full }) => (
                    <div key={field} className={full ? 'md:col-span-2' : ''}>
                      <label className="block text-[10px] text-white/30 uppercase tracking-widest font-bold mb-2">{label}</label>
                      {isEditing ? (
                        <input
                          value={value}
                          onChange={(e) => handleField(field, e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#4d7cff]/50"
                        />
                      ) : (
                        <div className="bg-white/[0.03] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white/70">{value || '—'}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Password & Security */}
              <div className="bg-[#0c1526] border border-white/[0.08] rounded-[24px] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">Password & Security</h3>
                  <button
                    onClick={() => { setShowPasswordForm(v => !v); setPwError(''); setPwSuccess(false); }}
                    className="text-xs font-bold text-[#4d7cff] border border-[#4d7cff]/30 px-4 py-1.5 rounded-xl hover:bg-[#4d7cff]/10 transition-all"
                  >
                    {showPasswordForm ? 'Cancel' : 'Change Password'}
                  </button>
                </div>

                {/* 2FA — coming soon */}
                <div className="flex items-center justify-between py-3 border-b border-white/5 mb-4">
                  <div>
                    <div className="text-sm font-medium">Two-Factor Authentication</div>
                    <div className="text-xs text-white/30">Add an extra layer of security</div>
                  </div>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/[0.06] text-white/30 uppercase tracking-widest">Soon</span>
                </div>

                {showPasswordForm && (
                  <div className="space-y-3">
                    {[
                      { label: 'Current Password', key: 'current', type: 'password' },
                      { label: 'New Password',      key: 'next',    type: 'password' },
                      { label: 'Confirm New',        key: 'confirm', type: 'password' },
                    ].map(({ label, key, type }) => (
                      <div key={key}>
                        <label className="block text-[10px] text-white/30 uppercase tracking-widest font-bold mb-1">{label}</label>
                        <input
                          type={type}
                          value={pwForm[key]}
                          onChange={e => setPwForm(prev => ({ ...prev, [key]: e.target.value }))}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#4d7cff]/50"
                        />
                      </div>
                    ))}
                    {pwError   && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2">{pwError}</div>}
                    {pwSuccess && <div className="text-xs text-green-400 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-2 flex items-center gap-2"><Check size={12}/> Password changed successfully</div>}
                    <button
                      onClick={handlePasswordChange}
                      disabled={pwLoading}
                      className="w-full bg-[#4d7cff] hover:bg-[#3d6cef] disabled:opacity-50 text-white text-sm font-bold py-2.5 rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      {pwLoading ? <><Loader size={14} className="animate-spin" /> Saving...</> : 'Update Password'}
                    </button>
                  </div>
                )}
              </div>

              {/* Favorites */}
              <div className="bg-[#0c1526] border border-white/[0.08] rounded-[24px] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold">My Favorites</h3>
                  <span className="text-xs text-white/30">{favorites.length} player{favorites.length !== 1 ? 's' : ''}</span>
                </div>

                {favsLoading ? (
                  <div className="flex items-center justify-center py-8 text-white/30">
                    <Loader size={16} className="animate-spin mr-2" /> Loading...
                  </div>
                ) : favorites.length === 0 ? (
                  <div className="text-center py-8 text-white/20">
                    <p className="text-sm">No favorites yet</p>
                    <p className="text-xs mt-1">Tap the heart icon on any player in the dashboard</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {favorites.map((fav) => {
                      const playerId = fav.player_id || fav.id;
                      const name     = fav.player_name || fav.name;
                      const pos      = fav.position    || fav.pos;
                      const team     = fav.team;
                      return (
                        <div key={playerId} className="flex items-center justify-between p-3 bg-white/[0.03] rounded-xl border border-white/5 group">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-[#4d7cff]/20 flex items-center justify-center text-xs font-bold text-[#4d7cff] overflow-hidden">
                              {fav.headshot_url
                                ? <img src={fav.headshot_url} alt={name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                                : getPlayerInitials(name)}
                            </div>
                            <div>
                              <div className="text-sm font-semibold">{name}</div>
                              <div className="text-xs text-white/30">{pos} · {team}</div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFavorite(playerId)}
                            className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-white/30 hover:bg-red-500/20 hover:text-red-400 transition-all opacity-0 group-hover:opacity-100"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Danger zone */}
              <div className="bg-[#0c1526] border border-red-500/20 rounded-[24px] p-6">
                <h3 className="font-bold text-red-400 mb-2">Danger Zone</h3>
                <p className="text-white/30 text-sm mb-4">These actions are permanent and cannot be undone.</p>
                <button
                  onClick={() => navigate('/logout')}
                  className="text-sm font-bold text-red-400 border border-red-500/30 px-5 py-2 rounded-xl hover:bg-red-500/10 transition-all"
                >
                  Log Out
                </button>
              </div>

            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Profile;